import { useQueryClient } from "@tanstack/react-query";
import {
    TASK_BULK_SAVED_SUBJECT,
    TASK_SAVED_SUBJECT,
    TaskBulkSavedEvent,
    TaskSavedEvent,
} from "@common/types/task-nats";
import { tasksQueryKeys, todoTasksCountQueryKey } from "@api/task.api";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { useScope } from "@state/scopeContext.lib";
import { useUser } from "./useUser";

/**
 * Subscribes to task.saved NATS events and keeps the task list / detail caches
 * up to date in real time when *other* users make changes.
 *
 * - "created" → invalidate list (refetches, picks up new task)
 * - "updated" → invalidate detail + list for that task
 * - Own changes (updatedByUserId === current user) are ignored because
 *   useUpdateTask / useCreateTask already manage the cache optimistically.
 */
export function useTaskLiveUpdates() {
    const { workspaceId, activeWorkspaceKey } = useScope();
    const queryClient = useQueryClient();
    const currentUser = useUser();
    const currentUserId = currentUser?.id;

    useNatsSubscription<{ data: TaskSavedEvent }>(
        workspaceId && activeWorkspaceKey
            ? `workspaces.${workspaceId}.notifications.${TASK_SAVED_SUBJECT}`
            : null,
        {
            onMessage: ({ data: event }) => {
                // Skip our own saves — optimistic updates already cover them
                if (event.updatedByUserId === currentUserId) {
                    return;
                }

                if (event.kind === "updated") {
                    queryClient.invalidateQueries({
                        queryKey: tasksQueryKeys.detail(
                            activeWorkspaceKey,
                            event.taskId,
                        ),
                        exact: true,
                    });
                }

                // Common invalidations for both created and updated events
                queryClient.invalidateQueries({
                    queryKey:
                        tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                    exact: true,
                });
                queryClient.invalidateQueries({
                    queryKey: todoTasksCountQueryKey(activeWorkspaceKey),
                    exact: true,
                });
            },
            onError: (error) => {
                // eslint-disable-next-line no-console
                console.error(
                    "useTaskLiveUpdates: failed to process NATS message",
                    error,
                );
            },
        },
    );

    useNatsSubscription<{ data: TaskBulkSavedEvent }>(
        workspaceId && activeWorkspaceKey
            ? `workspaces.${workspaceId}.notifications.${TASK_BULK_SAVED_SUBJECT}`
            : null,
        {
            onMessage: ({ data: event }) => {
                if (event.updatedByUserId === currentUserId) {
                    return;
                }

                queryClient.invalidateQueries({
                    queryKey:
                        tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                    exact: true,
                });
                queryClient.invalidateQueries({
                    queryKey: todoTasksCountQueryKey(activeWorkspaceKey),
                    exact: true,
                });
            },
            onError: (error) => {
                // eslint-disable-next-line no-console
                console.error(
                    "useTaskLiveUpdates: failed to process NATS bulk message",
                    error,
                );
            },
        },
    );
}
